import React from "react"
import { cn } from "../utils"
import { cva, VariantProps } from "class-variance-authority"
import { NoteIcon } from "./note-icon"
import { NoteContext } from "./note-context"
import { NoteHeading } from "./note-heading"
import { NoteContent } from "./note-content"
import { NoteDismiss } from "./note-dismiss"
import { NoteActions } from "./note-actions"
import { NoteContentLeading } from "./note-content-leading"
import { NoteInlineChildren } from "./note-inline-children"
import { restoreFocusBeforeUnmount, type FocusTarget } from "./note-focus"

// =============================================================================
// Styles
// =============================================================================
const sharedStackedLayoutStyles = [
  "p-4",
  "grid-cols-[auto_1fr_auto] items-start",
  "[grid-template-areas:'icon_._dismiss'_'content_content_content'_'actions_actions_actions']",
  "[&>[data-slot=note-icon]]:mb-4",
  "[&>[data-slot=note-content-leading]]:mb-4",
  "[&>[data-slot=note-dismiss]]:mb-4",
  "[&>[data-slot=note-actions]]:mt-4"
]

const noteStyles = cva(
  [
    "@container/note w-full",
    "rounded-lg ring-1 ring-inset",
    "text-left text-sm leading-6",
    "[&_a:not([data-slot])]:outline-2 [&_a:not([data-slot])]:outline-offset-2 [&_a:not([data-slot])]:outline-transparent [&_a:not([data-slot])]:underline [&_a:not([data-slot])]:rounded-[0.25rem] [&_a:not([data-slot])]:font-medium [&_a:not([data-slot])]:transition-colors [&_a:not([data-slot])]:hover:text-gray-800 [&_a:not([data-slot])]:hover:decoration-gray-800 [&_a:not([data-slot])]:focus-visible:outline-gray-900",
    "[&>[data-slot=note-content-leading]]:shrink-0"
  ],
  {
    variants: {
      variant: {
        info: [
          "ring-warm-white-300 bg-warm-white-100",
          "[&_[data-slot=note-icon]]:text-gray-700"
        ],
        upsell: [
          "ring-blue-200 bg-blue-50",
          "[&_[data-slot=note-icon]]:text-blue-700"
        ],
        success: [
          "ring-green-200 bg-green-50",
          "[&_[data-slot=note-icon]]:text-green-700"
        ],
        warning: [
          "ring-system-yellow-200 bg-system-yellow-50",
          "[&_[data-slot=note-icon]]:text-system-yellow-700"
        ],
        error: [
          "ring-red-200 bg-red-50",
          "[&_[data-slot=note-icon]]:text-red-700"
        ]
      },
      layout: {
        horizontal: "",
        stacked: "",
        inline: "[--note-content-min-width:30ch]"
      }
    },
    defaultVariants: {
      variant: "info",
      layout: "horizontal"
    }
  }
)

const noteLayoutStyles = cva("", {
  variants: {
    layout: {
      horizontal: [
        "grid",
        ...sharedStackedLayoutStyles,
        "@sm/note:gap-y-4",
        "@sm/note:[grid-template-areas:'icon_content_dismiss']",
        "@sm/note:has-[>[data-slot=note-actions]]:[grid-template-areas:'icon_content_dismiss'_'._actions_.']",
        "@sm/note:[&>[data-slot=note-icon]]:mb-0 @sm/note:[&>[data-slot=note-icon]]:mr-4",
        "@sm/note:[&>[data-slot=note-content-leading]]:mb-0 @sm/note:[&>[data-slot=note-content-leading]]:mr-4",
        "@sm/note:[&>[data-slot=note-dismiss]]:mb-0 @sm/note:[&>[data-slot=note-dismiss]]:ml-4",
        "@sm/note:[&>[data-slot=note-actions]]:mt-0"
      ],
      stacked: ["grid", ...sharedStackedLayoutStyles],
      inline: [
        "relative flex flex-wrap items-start gap-2 p-4",
        "[&_[data-slot=note-content]]:mt-[0.125rem]",
        "has-[>[data-slot=note-dismiss]]:pr-12",
        "[&>[data-slot=note-dismiss]]:absolute [&>[data-slot=note-dismiss]]:top-5 [&>[data-slot=note-dismiss]]:right-4"
      ]
    }
  },
  defaultVariants: {
    layout: "horizontal"
  }
})

const inlineBodyStyles = [
  "flex flex-1 flex-wrap items-start gap-x-2 gap-y-3",
  "min-w-[min(var(--note-content-min-width),100%)]",
  // Progressively enhance when calc-size() is supported by taking the min of the content size compared to the default min width
  "supports-[min-width:calc-size(max-content,size)]:min-w-[calc-size(max-content,min(size,var(--note-content-min-width),100%))]"
]

// =============================================================================
// Props
// =============================================================================
export type NoteProps = {
  children: React.ReactNode
  className?: string
  /** Shows or hides the Note inside a live region, so screen readers announce it when it appears. Pass the condition here instead of conditionally rendering the Note. */
  isShown?: boolean
} & VariantProps<typeof noteStyles> &
  VariantProps<typeof noteLayoutStyles>

// =============================================================================
// Component
// =============================================================================
export function NoteRoot({
  children,
  className,
  variant,
  layout,
  isShown
}: NoteProps) {
  const resolvedVariant = variant ?? "info"
  const resolvedLayout = layout ?? "horizontal"
  const headingId = React.useId()
  const noteRef = React.useRef<HTMLElement>(null)
  const [hasHeading, setHasHeading] = React.useState(false)
  const [isDismissed, setIsDismissed] = React.useState(false)

  const registerHeading = React.useCallback(() => {
    setHasHeading(true)
    return () => setHasHeading(false)
  }, [])

  const dismiss = React.useCallback((focusTarget?: FocusTarget) => {
    if (noteRef.current) restoreFocusBeforeUnmount(noteRef.current, focusTarget)
    setIsDismissed(true)
  }, [])

  React.useEffect(() => {
    if (isShown === false) setIsDismissed(false)
  }, [isShown])

  const isLiveRegion = isShown !== undefined

  if (isDismissed && !isLiveRegion) return null

  // An empty live region stays mounted so screen readers announce the Note when it's shown.
  // It's visually hidden rather than display: none or display: contents, which drop it from the accessibility tree.
  const isHidden = isLiveRegion && (!isShown || isDismissed)

  return (
    <NoteContext.Provider
      value={{
        variant: resolvedVariant,
        layout: resolvedLayout,
        headingId,
        registerHeading,
        dismiss
      }}
    >
      <section
        className={
          isHidden
            ? "sr-only"
            : cn(
                noteStyles({
                  variant: resolvedVariant,
                  layout: resolvedLayout
                }),
                className
              )
        }
        aria-labelledby={hasHeading ? headingId : undefined}
        data-slot="note"
        ref={noteRef}
      >
        <div
          className={noteLayoutStyles({ layout })}
          role={isLiveRegion ? "status" : undefined}
        >
          {!isHidden &&
            (resolvedLayout === "inline" ? (
              <NoteInlineChildren className={cn(inlineBodyStyles)}>
                {children}
              </NoteInlineChildren>
            ) : (
              children
            ))}
        </div>
      </section>
    </NoteContext.Provider>
  )
}

// =============================================================================
// Compound component
// =============================================================================
interface NoteComponent {
  (props: NoteProps): React.JSX.Element | null
  Icon: typeof NoteIcon
  ContentLeading: typeof NoteContentLeading
  Heading: typeof NoteHeading
  Content: typeof NoteContent
  Dismiss: typeof NoteDismiss
  Actions: typeof NoteActions
}

export const Note: NoteComponent = Object.assign(NoteRoot, {
  Icon: NoteIcon,
  ContentLeading: NoteContentLeading,
  Heading: NoteHeading,
  Content: NoteContent,
  Dismiss: NoteDismiss,
  Actions: NoteActions
} as const)
